// src/components/layout/Sidebar.js
import React, { useState, useEffect } from "react";
import {
  SwipeableDrawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Box,
  Collapse,
  useMediaQuery,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
} from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import { Icon } from "../icons";
import { COLORS, FONT_FAMILY, TYPOGRAPHY } from "../constants/color";
import Logo from "../assets/Arconest_logo.png";
import { useFlowToggle } from "../context/flowToggleContext";
import axios from "axios";
import { url } from "../constants/url";
import { useAuth } from "../context/authContext";
import { toast } from "react-hot-toast";

// Technical Observation Navigation Items
const getTechnicalNavItems = (userRole) => {
  const allItems = {
    ADMIN: [
      { label: "Dashboard", icon: "dashboard", href: "/technical/dashboard" },
      { label: "Calendar", icon: "calendar", href: "/technical/calendar" },
      { label: "Clients", icon: "clients", href: "/technical/clients" },
      { label: "Engineers", icon: "users", href: "/technical/engineers" },
      {
        label: "Add New Project",
        icon: "squarePlus",
        href: "/technical/add-project",
      },
      {
        label: "Project Folder",
        icon: "dashboard",
        children: [
          {
            label: "Client Requests",
            icon: "file-text",
            href: "/projects/fir-requests",
          },
          {
            label: "In Progress",
            icon: "calendar",
            href: "/technical/projects/in-progress",
          },
          {
            label: "Completed",
            icon: "calendarCheck",
            href: "/technical/projects/completed",
          },
        ],
      },
      {
        label: "Master Mappings",
        icon: "sitemap",
        children: [
          {
            label: "Residential",
            icon: "home1",
            href: "/technical/mapping/residential",
          },
          {
            label: "Commercial",
            icon: "building",
            href: "/technical/mapping/commercial",
          },
          {
            label: "Industrial",
            icon: "factory",
            href: "/technical/mapping/industrial",
          },
          {
            label: "Hospitality",
            icon: "hotel",
            href: "/technical/mapping/hospitality",
          },
          {
            label: "Manage FIR Mappings",
            icon: "sitemap",
            href: "/settings/manage-fir-mappings",
          },
        ],
      },
      {
        label: "Client's Plan History",
        icon: "credit-card",
        href: "/technical/credits",
      },
      {
        label: "Settings",
        icon: "settings",
        children: [
          {
            label: "Export Data",
            icon: "download",
            href: "/technical/settings/export",
          },
          {
            label: "Manage Dummy Stats",
            icon: "edit",
            href: "/technical/settings/dummy-stats",
          },
        ],
      },
    ],

    SUPERADMIN: [
      { label: "Dashboard", icon: "dashboard", href: "/technical/dashboard" },
      { label: "Calendar", icon: "calendar", href: "/technical/calendar" },
      { label: "Clients", icon: "clients", href: "/technical/clients" },
      { label: "Engineers", icon: "users", href: "/technical/engineers" },
      {
        label: "Add New Project",
        icon: "squarePlus",
        href: "/technical/add-project",
      },
      {
        label: "Project Folder",
        icon: "dashboard",
        children: [
          {
            label: "Client Requests",
            icon: "file-text",
            href: "/projects/fir-requests",
          },
          {
            label: "In Progress",
            icon: "calendar",
            href: "/technical/projects/in-progress",
          },
          {
            label: "Completed",
            icon: "calendarCheck",
            href: "/technical/projects/completed",
          },
        ],
      },
      {
        label: "Master Mappings",
        icon: "sitemap",
        children: [
          {
            label: "Residential",
            icon: "home1",
            href: "/technical/mapping/residential",
          },
          {
            label: "Commercial",
            icon: "building",
            href: "/technical/mapping/commercial",
          },
          {
            label: "Industrial",
            icon: "factory",
            href: "/technical/mapping/industrial",
          },
          {
            label: "Hospitality",
            icon: "hotel",
            href: "/technical/mapping/hospitality",
          },
          {
            label: "Manage FIR Mappings",
            icon: "sitemap",
            href: "/settings/manage-fir-mappings",
          },
        ],
      },
      {
        label: "Credits History",
        icon: "credit-card",
        href: "/technical/credits",
      },
      {
        label: "Settings",
        icon: "settings",
        children: [
          {
            label: "Export Data",
            icon: "download",
            href: "/technical/settings/export",
          },
          {
            label: "Manage Dummy Stats",
            icon: "edit",
            href: "/technical/settings/dummy-stats",
          },
        ],
      },
    ],

    ENGINEER: [
      { label: "Dashboard", icon: "dashboard", href: "/technical/dashboard" },
      { label: "Calendar", icon: "calendar", href: "/technical/calendar" },

      {
        label: "Project Folder",
        icon: "dashboard",
        children: [
          {
            label: "Client Requests",
            icon: "file-text",
            href: "/projects/fir-requests",
          },
          {
            label: "In Progress",
            icon: "calendar",
            href: "/technical/projects/in-progress",
          },
          {
            label: "Completed",
            icon: "calendarCheck",
            href: "/technical/projects/completed",
          },
        ],
      },
      {
        label: "Mapping",
        icon: "sitemap",
        children: [
          {
            label: "Residential",
            icon: "home1",
            href: "/technical/mapping/residential",
          },
          {
            label: "Commercial",
            icon: "building",
            href: "/technical/mapping/commercial",
          },
          {
            label: "Industrial",
            icon: "factory",
            href: "/technical/mapping/industrial",
          },
          {
            label: "Hospitality",
            icon: "hotel",
            href: "/technical/mapping/hospitality",
          },
        ],
      },
    ],
  };

  // Map your user roles to the technical navigation structure
  if (userRole === "SUPERADMIN") return allItems.SUPERADMIN;
  if (userRole === "ADMIN") return allItems.ADMIN;
  if (userRole === "ENGINEER") return allItems.ENGINEER;

  // Default for no role or unknown role
  return [];
};

// Navigation Items based on user role (unchanged)
const getNavItems = (user) => {
  const userRole = user?.role || "CLIENT";
  const allItems = {
    ADMIN: [
      { label: "Dashboard", icon: "dashboard", href: "/dashboard" },
      { label: "Calendar", icon: "calendar", href: "/calendar" },
      { label: "Clients", icon: "clients", href: "/clients" },
      { label: "Engineers", icon: "engineers", href: "/engineers" },
      {
        label: "Projects",
        icon: "projects",
        children: [
          { label: "All Projects", icon: "folder-open", href: "/projects/all" },
          { label: "Unassigned", icon: "clock", href: "/projects/unassigned" },
          {
            label: "Assigned",
            icon: "check-circle",
            href: "/projects/assigned",
          },
          // {
          //   label: "Visited",
          //   icon: "completed",
          //   href: "/projects/completed",
          // },
          // { label: "Visited", icon: "completed", href: "/projects/visited" },
          {
            label: "Signature Pending",
            icon: "pen-line",
            href: "/projects/signature-pending",
          },
          {
            label: "Final Reports",
            icon: "file-text",
            href: "/projects/final-reports",
          },
        ],
      },
      {
        label: "Progreess Reports",
        icon: "trending",
        children: [
          {
            label: "InHouse Projects",
            icon: "wallpaper",
            href: "/progress/inhouse",
          },
          { label: "DPR", icon: "clock1", href: "/progress/dpr" },
          {
            label: "WPR",
            icon: "message",
            href: "/progress/wpr",
          },
          // {
          //   label: "Visited",
          //   icon: "completed",
          //   href: "/projects/completed",
          // },
          // { label: "Visited", icon: "completed", href: "/projects/visited" },
          {
            label: "WMR",
            icon: "message",
            href: "/progress/wmr",
          },
          {
            label: "MPR",
            icon: "calendar2",
            href: "/progress/mpr",
          },
          {
            label: "Site Visit Reports",
            icon: "building",
            href: "/self-visit-reports",
          },
        ],
      },
      {
        label: "Credits History",
        icon: "file-text",
        href: "/history",
      },
      {
        label: "Settings",
        icon: "settings",
        children: [
          {
            label: "Checklists",
            icon: "clipboard-list",
            href: "/settings/checklists",
          },
          { label: "Export Data", icon: "download", href: "/settings/export" },
          { label: "General Settings", icon: "cog", href: "/settings/general" },
        ],
      },
    ],

    ENGINEER: [
      { label: "Dashboard", icon: "dashboard", href: "/dashboard" },
      { label: "Calendar", icon: "calendar", href: "/calendar" },
      {
        label: "Projects",
        icon: "projects",
        children: [
          { label: "All Projects", icon: "folder-open", href: "/projects/all" },
          // { label: "Unassigned", icon: "clock", href: "/projects/unassigned" },
          {
            label: "Assigned",
            icon: "check-circle",
            href: "/projects/assigned",
          },
          // {
          //   label: "Completed",
          //   icon: "check-circle",
          //   href: "/projects/completed",
          // },
          //{ label: "Visited", icon: "completed", href: "/projects/visited" },
          {
            label: "Signature Pending",
            icon: "pen-line",
            href: "/projects/signature-pending",
          },
          {
            label: "Final Reports",
            icon: "file-text",
            href: "/projects/final-reports",
          },
        ],
      },
      {
        label: "Progreess Reports",
        icon: "trending",
        children: [
          {
            label: "InHouse Projects",
            icon: "wallpaper",
            href: "/progress/inhouse",
          },
          { label: "DPR", icon: "clock1", href: "/progress/dpr" },
          {
            label: "WPR",
            icon: "message",
            href: "/progress/wpr",
          },
          // {
          //   label: "Visited",
          //   icon: "completed",
          //   href: "/projects/completed",
          // },
          // { label: "Visited", icon: "completed", href: "/projects/visited" },
          {
            label: "WMR",
            icon: "message",
            href: "/progress/wmr",
          },
          {
            label: "MPR",
            icon: "calendar2",
            href: "/progress/mpr",
          },
          {
            label: "Site Visit Reports",
            icon: "building",
            href: "/self-visit-reports",
          },
        ],
      },
      // {
      //   label: "Weekly Reports",
      //   icon: "file-bar-chart",
      //   href: "/weekly-reports",
      // },
      {
        label: "Settings",
        icon: "settings",
        children: [
          {
            label: "Checklists",
            icon: "clipboard-list",
            href: "/settings/checklists",
          },
          { label: "General Settings", icon: "cog", href: "/settings/general" },
        ],
      },
    ],

    CLIENT: [
      { label: "Dashboard", icon: "dashboard", href: "/dashboard" },
      { label: "Calendar", icon: "calendar", href: "/calendar" },

      {
        label: "Projects",
        icon: "projects",
        children: [
          {
            label: "Add Site Check",
            icon: "plus",
            href: "/projects/add-site-check",
          },
          { label: "All Projects", icon: "folder-open", href: "/projects/all" },
          { label: "Unassigned", icon: "clock", href: "/projects/unassigned" },
          {
            label: "Assigned",
            icon: "check-circle",
            href: "/projects/assigned",
          },
          { label: "Visited", icon: "completed", href: "/projects/visited" },
          {
            label: "Final Reports",
            icon: "file-text",
            href: "/projects/final-reports",
          },
        ],
      },
      {
        label: "Credits",
        icon: "credit-card",
        children: [
          {
            label: "Purchase Credits",
            icon: "credit-card",
            href: "/buy-credits",
          },
          {
            label: "Credit History",
            icon: "file-text",
            href: "/history",
          },
        ],
      },
    ],

    INDIVIDUAL: [
      { label: "Dashboard", icon: "dashboard", href: "/dashboard" },
      {
        label: "Requests",
        icon: "projects",
        children: [
          { label: "Create Request", icon: "plus", href: "/fir/create-wizard" },
          { label: "All Requests", icon: "folder-open", href: "/projects/all" },
        ],
      },
      {
        label: "Payment History",
        icon: "file-text",
        href: "/history",
      },
    ],

    SUPERADMIN: [
      { label: "Dashboard", icon: "dashboard", href: "/dashboard" },
      { label: "Calendar", icon: "calendar", href: "/calendar" },
      { label: "Clients", icon: "clients", href: "/clients" },
      { label: "Engineers", icon: "engineers", href: "/engineers" },
      {
        label: "Projects",
        icon: "projects",
        children: [
          { label: "All Projects", icon: "folder-open", href: "/projects/all" },
          { label: "Unassigned", icon: "clock", href: "/projects/unassigned" },
          {
            label: "Assigned",
            icon: "check-circle",
            href: "/projects/assigned",
          },
          // {
          //   label: "Visited",
          //   icon: "completed",
          //   href: "/projects/completed",
          // },
          // { label: "Visited", icon: "completed", href: "/projects/visited" },
          {
            label: "Signature Pending",
            icon: "pen-line",
            href: "/projects/signature-pending",
          },
          {
            label: "Final Reports",
            icon: "file-text",
            href: "/projects/final-reports",
          },
        ],
      },
      {
        label: "Progreess Reports",
        icon: "trending",
        children: [
          {
            label: "InHouse Projects",
            icon: "wallpaper",
            href: "/progress/inhouse",
          },
          { label: "DPR", icon: "clock1", href: "/progress/dpr" },
          {
            label: "WPR",
            icon: "message",
            href: "/progress/wpr",
          },
          // {
          //   label: "Visited",
          //   icon: "completed",
          //   href: "/projects/completed",
          // },
          // { label: "Visited", icon: "completed", href: "/projects/visited" },
          {
            label: "WMR",
            icon: "message",
            href: "/progress/wmr",
          },
          {
            label: "MPR",
            icon: "calendar2",
            href: "/progress/mpr",
          },
          {
            label: "Site Visit Reports",
            icon: "building",
            href: "/self-visit-reports",
          },
        ],
      },
      {
        label: "Credits History",
        icon: "file-text",
        href: "/history",
      },
      {
        label: "Settings",
        icon: "settings",
        children: [
          {
            label: "Checklists",
            icon: "clipboard-list",
            href: "/settings/checklists",
          },
          { label: "Export Data", icon: "download", href: "/settings/export" },
          { label: "General Settings", icon: "cog", href: "/settings/general" },
        ],
      },
    ],
  };

  // Map your user roles to the navigation structure
  if (userRole === "SUPERADMIN") return allItems.SUPERADMIN;
  if (userRole === "ADMIN") return allItems.ADMIN;
  if (userRole === "ENGINEER") return allItems.ENGINEER;
  if (userRole === "CLIENT") {
    if (user?.clientRole?.roleType === "Individual") {
      return allItems.INDIVIDUAL;
    }
    return allItems.CLIENT;
  }

  // Default for no role or unknown role
  return allItems.CLIENT;
};

// src/components/layout/Sidebar.js (updated to hide all scrollbars)
const Sidebar = ({ open, user, onClose }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("lg"));
  const { token } = useAuth();
  const [masterMappings, setMasterMappings] = useState([]);
  const [addMappingOpen, setAddMappingOpen] = useState(false);
  const [newMappingName, setNewMappingName] = useState("");

  useEffect(() => {
    const fetchMappings = async () => {
      if (!token) return;
      try {
        const response = await axios.get(`${url}/api/master-mappings/all`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (response.data.success) {
          setMasterMappings(response.data.data);
        }
      } catch (error) {
        console.error("Error fetching master mappings in sidebar:", error);
      }
    };

    fetchMappings();

    const handleRefresh = () => fetchMappings();
    window.addEventListener("refreshMasterMappings", handleRefresh);
    return () =>
      window.removeEventListener("refreshMasterMappings", handleRefresh);
  }, [token, location.pathname]);

  const [expandedItems, setExpandedItems] = useState([
    "Projects",
    "Requests",
    "Settings",
    "Progreess Reports",
    "Credits",
    // Technical flow folders - keep all open by default
    "Project Folder",
    "Master Mappings",
  ]);
  const { isTechnicalFlow } = useFlowToggle();

  const getDynamicTechnicalNavItems = (userRole) => {
    const technicalItems = getTechnicalNavItems(userRole);

    // Find the "Mapping" section and update its children
    const mappingSection = technicalItems.find(
      (item) => item.label === "Mapping" || item.label === "Master Mappings",
    );
    if (mappingSection) {
      mappingSection.label = "Master Mappings";

      // Build dynamic list from DB (deduplicated by name)
      const uniqueMappings = [];
      const seenNames = new Set();

      masterMappings.forEach((m) => {
        const lowerName = m.mappingName.toLowerCase().trim();
        if (!seenNames.has(lowerName)) {
          seenNames.add(lowerName);
          uniqueMappings.push(m);
        }
      });

      const dbItems = uniqueMappings.map((m) => ({
        label: m.mappingName.charAt(0).toUpperCase() + m.mappingName.slice(1),
        icon:
          m.mappingName.toLowerCase() === "residential"
            ? "home1"
            : m.mappingName.toLowerCase() === "commercial"
              ? "building"
              : m.mappingName.toLowerCase() === "industrial"
                ? "factory"
                : m.mappingName.toLowerCase() === "hospitality"
                  ? "hotel"
                  : "sitemap",
        href: `/technical/mapping/${m.mappingName.toLowerCase()}`,
        isExisting: true,
      }));

      // Add "Create New" option only for ADMIN/SUPERADMIN
      if (
        userRole === "ADMIN" ||
        userRole === "SUPERADMIN" ||
        userRole === "ENGINEER"
      ) {
        const createNewItem = {
          label: "Create New",
          icon: "plus",
          onClick: () => setAddMappingOpen(true),
          isAction: true,
        };
        mappingSection.children = [createNewItem, ...dbItems];
      } else {
        mappingSection.children = [...dbItems];
      }

      if (
        userRole === "ADMIN" ||
        userRole === "SUPERADMIN" ||
        userRole === "ENGINEER"
      ) {
        mappingSection.children.push({
          label: "Manage FIR Mappings",
          icon: "sitemap",
          href: "/settings/manage-fir-mappings",
        });
      }
    }

    return technicalItems;
  };

  const drawerWidth = 240;

  // Close sidebar on mobile when route changes
  useEffect(() => {
    if (isMobile && open) {
      onClose?.();
    }
    // We only want to trigger this effect when the pathname changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Get navigation items based on user role and flow state
  const userRole = user?.role || "CLIENT";
  const navItems =
    isTechnicalFlow && userRole !== "CLIENT"
      ? getDynamicTechnicalNavItems(userRole)
      : getNavItems(user);

  const toggleExpand = (label) => {
    setExpandedItems((prev) =>
      prev.includes(label)
        ? prev.filter((item) => item !== label)
        : [...prev, label],
    );
  };

  // const isActive = (href) => {
  //   if (!href) return false;
  //   return (
  //     location.pathname === href || location.pathname.startsWith(`${href}/`)
  //   );
  // };
  const isActive = (href) => {
    if (!href) return false;

    // Handle exact matches for progress routes - each tab independent
    if (href === "/progress/inhouse") {
      return (
        location.pathname === "/progress/inhouse" ||
        location.pathname.startsWith("/progress/edit-report") ||
        location.pathname.startsWith("/progress/add-dpr") ||
        location.pathname.startsWith("/progress/edit-dpr") ||
        location.pathname.startsWith("/progress/view-dpr")
      );
    } else if (href === "/progress/dpr") {
      return location.pathname === "/progress/dpr";
    } else if (href === "/progress/wpr") {
      return location.pathname.startsWith("/progress/wpr");
    } else if (href === "/progress/wmr") {
      return location.pathname.startsWith("/progress/wmr");
    } else if (href === "/progress/mpr") {
      return location.pathname.startsWith("/progress/mpr");
    } else if (href === "/self-visit-reports") {
      return location.pathname.startsWith("/self-visit-reports");
    }

    return (
      location.pathname === href || location.pathname.startsWith(`${href}/`)
    );
  };

  const isParentActive = (children) => {
    if (!children) return false;
    return children.some((child) => isActive(child.href));
  };

  const handleNavigation = (href) => {
    navigate(href);
    if (isMobile) {
      onClose?.();
    }
  };

  return (
    <SwipeableDrawer
      variant={isMobile ? "temporary" : "persistent"}
      open={open}
      onClose={onClose}
      onOpen={() => {}} // Simple placeholder required by SwipeableDrawer
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        zIndex: isMobile ? 1300 : 1,
        "& .MuiDrawer-paper": {
          width: drawerWidth,
          boxSizing: "border-box",
          // NO manual z-index or fixed position here - let MUI handle it
          overflow: "hidden",
          height: "100vh",
          display: "flex",
          flexDirection: "column",
          zIndex: isMobile ? 1300 : 1,
          "&::-webkit-scrollbar": {
            display: "none",
          },
          scrollbarWidth: "none",
          "-ms-overflow-style": "none",
        },
      }}
    >
      {/* Logo Section - Fixed at top */}
      <Box
        sx={{
          p: 2,
          borderBottom: `1px solid ${COLORS.BORDER}`,
          height: 64,
          display: "flex",
          alignItems: "center",
          flexShrink: 0, // Prevent shrinking
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Box
            component="img"
            src={Logo}
            alt="Arconest Logo"
            sx={{
              height: 47,
              width: 131,
              objectFit: "contain",
              flexShrink: 0,
            }}
          />
        </Box>
      </Box>

      {/* Navigation - Scrollable but with hidden scrollbar */}
      <Box
        sx={{
          flex: 1,
          overflowY: "auto", // Allow scrolling
          overflowX: "hidden", // Hide horizontal scroll
          // HIDE VERTICAL SCROLLBAR COMPLETELY
          "&::-webkit-scrollbar": {
            display: "none", // Chrome/Safari/Edge
            width: 0,
            height: 0,
          },
          "&::-webkit-scrollbar-track": {
            display: "none",
          },
          "&::-webkit-scrollbar-thumb": {
            display: "none",
          },
          // Firefox
          scrollbarWidth: "none",
          // IE/Edge
          "-ms-overflow-style": "none",
        }}
      >
        <List
          sx={{
            px: open ? 0 : 0,
            py: 1,
            minWidth: open ? 240 : 0,
            "& .MuiListItem-root": {
              padding: 0,
            },
          }}
        >
          {navItems.map((item) => {
            const hasChildren = item.children && item.children.length > 0;
            const active = hasChildren
              ? isParentActive(item.children)
              : isActive(item.href);

            return (
              <Box key={item.label}>
                <ListItem disablePadding sx={{ mb: 0.5 }}>
                  <ListItemButton
                    onClick={() =>
                      hasChildren
                        ? toggleExpand(item.label)
                        : handleNavigation(item.href)
                    }
                    sx={{
                      borderRadius: 0,
                      py: 1.5,
                      px: 2,
                      backgroundColor: "transparent",
                      "&:hover": {
                        backgroundColor: COLORS.HOVER_BACKGROUND,
                      },
                      justifyContent: open ? "flex-start" : "center",
                      minHeight: 48,
                      borderLeft: "none",
                      marginLeft: 0,
                      marginRight: 0,
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 0,
                        mr: open ? 2 : 0,
                        justifyContent: "center",
                      }}
                    >
                      <Icon
                        name={item.icon}
                        size={18}
                        color={active ? COLORS.PRIMARY : COLORS.TEXT}
                      />
                    </ListItemIcon>

                    {open && (
                      <>
                        <ListItemText
                          primary={item.label}
                          primaryTypographyProps={{
                            fontFamily: FONT_FAMILY,
                            ...TYPOGRAPHY.H4,
                            fontWeight: active ? 600 : 500,
                            color: active ? COLORS.PRIMARY : COLORS.TEXT,
                          }}
                          sx={{
                            opacity: open ? 1 : 0,
                            margin: 0,
                          }}
                        />

                        {hasChildren && (
                          <Icon
                            name={
                              expandedItems.includes(item.label)
                                ? "expand-less"
                                : "expand-more"
                            }
                            size={16}
                            color={active ? COLORS.PRIMARY : COLORS.TEXT}
                          />
                        )}
                      </>
                    )}
                  </ListItemButton>
                </ListItem>

                {/* Children - Only show when sidebar is open */}
                {open && hasChildren && expandedItems.includes(item.label) && (
                  <Collapse in={expandedItems.includes(item.label)}>
                    <List
                      sx={{
                        pl: 2,
                        py: 0.5,
                        borderLeft: `2px solid ${COLORS.LIGHT_SECONDARY}`,
                        marginLeft: "20px",
                      }}
                    >
                      {item.children.map((child) => {
                        const childActive = isActive(child.href);
                        return (
                          <ListItem key={child.label} disablePadding>
                            <ListItemButton
                              onClick={() => {
                                if (child.isAction && child.onClick) {
                                  child.onClick();
                                } else {
                                  handleNavigation(child.href);
                                }
                              }}
                              sx={{
                                borderRadius: 0,
                                py: 1.2,
                                px: 1,
                                backgroundColor: "transparent",
                                "&:hover": {
                                  backgroundColor: COLORS.HOVER_BACKGROUND,
                                },
                                marginLeft: "-2px",
                                borderLeft: "none",
                                position: "relative",
                                "& .MuiListItemIcon-root": {
                                  minWidth: 28,
                                  paddingLeft: 0,
                                },
                              }}
                            >
                              <ListItemIcon sx={{ minWidth: 28, pl: 0 }}>
                                <Icon
                                  name={child.icon}
                                  size={16}
                                  color={
                                    childActive ? COLORS.PRIMARY : COLORS.TEXT
                                  }
                                />
                              </ListItemIcon>

                              <ListItemText
                                primary={child.label}
                                primaryTypographyProps={{
                                  fontFamily: FONT_FAMILY,
                                  ...TYPOGRAPHY.H4,
                                  fontWeight: childActive ? 600 : 400,
                                  color: childActive
                                    ? COLORS.PRIMARY
                                    : COLORS.TEXT,
                                }}
                                sx={{
                                  margin: 0,
                                  ml: 0.5,
                                }}
                              />
                            </ListItemButton>
                          </ListItem>
                        );
                      })}
                    </List>
                  </Collapse>
                )}
              </Box>
            );
          })}
        </List>
      </Box>

      {/* Add New Mapping Dialog */}
      <Dialog
        open={addMappingOpen}
        onClose={() => setAddMappingOpen(false)}
        PaperProps={{
          sx: { borderRadius: "12px", p: 1, minWidth: "400px" },
        }}
      >
        <DialogTitle
          sx={{ ...TYPOGRAPHY.H3, color: COLORS.PRIMARY, fontWeight: 700 }}
        >
          Create New Master Mapping
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2, color: COLORS.GREY_COLOR }}>
            Enter a name for the new project category (e.g., Government,
            Infrastructure).
          </Typography>
          <TextField
            autoFocus
            fullWidth
            label="Mapping Name"
            variant="outlined"
            value={newMappingName}
            onChange={(e) => setNewMappingName(e.target.value)}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
              },
            }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            onClick={() => setAddMappingOpen(false)}
            sx={{ color: COLORS.TEXT, textTransform: "none", fontWeight: 600 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={!newMappingName.trim()}
            onClick={async () => {
              const name = newMappingName.trim();
              if (!name) return;

              try {
                // Save to DB immediately
                await axios.post(
                  `${url}/api/master-mappings/add`,
                  { mappingName: name, components: [] },
                  { headers: { Authorization: `Bearer ${token}` } },
                );

                // Refresh sidebar
                window.dispatchEvent(new Event("refreshMasterMappings"));

                // Navigate
                handleNavigation(`/technical/mapping/${name.toLowerCase()}`);
                setAddMappingOpen(false);
                setNewMappingName("");
                toast.success("Master mapping category created");
              } catch (error) {
                console.error("Error creating master mapping:", error);
                toast.error("Failed to create master mapping category");
              }
            }}
            sx={{
              backgroundColor: COLORS.SECONDARY,
              textTransform: "none",
              fontWeight: 600,
              px: 3,
              "&:hover": { backgroundColor: COLORS.PRIMARY },
            }}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>
    </SwipeableDrawer>
  );
};

export default Sidebar;
